export const getMinMaxKeys = (table_column) => {
  const prefix = table_column.section_abbreviation?.toLowerCase() || ''
  const suffix = table_column.accessor_key.slice(prefix.length)

  const minKey = `${prefix}Min${suffix}`
  const maxKey = `${prefix}Max${suffix}`

  return { minKey, maxKey }
}

export const minMaxParamFilters = ['date', 'min_max', 'min_max_percent', 'time_in_seconds']

// Check for operator filters that don't require values (is_empty, is_not_empty)
// Note: 'contains' operator requires a value, so it's handled separately
const hasOperatorFilter = (accessorKey, params) => {
  const operatorKey = `${accessorKey}Operator`
  const operatorValue = params[operatorKey]
  return operatorValue && operatorValue !== '' && operatorValue !== 'contains'
}

// Function to check if a column has active filters
// Raw segment column -> the params key its account codes arrive under, in account-string order.
// Exported so anything that has to reason about "which param holds this column's
// filter?" — filter chips, the text-chart data-field picker — reads one mapping.
export const ACCOUNT_SEGMENT_PARAM_KEYS = {
  org: 'orgs',
  object: 'objects',
  project: 'projects',
  seg_one: 'segOnes',
  seg_two: 'segTwos',
  seg_three: 'segThrees',
  seg_four: 'segFours',
  seg_five: 'segFives',
  seg_six: 'segSixes',
  seg_seven: 'segSevens',
  seg_eight: 'segEights'
}

// Segments these params filter on that the given columns don't show — nothing on screen would say so.
export const hiddenFilteredAccountColumns = (params, displayedColumnNames = []) =>
  Object.entries(ACCOUNT_SEGMENT_PARAM_KEYS)
    .filter(([columnName, paramKey]) => {
      if (displayedColumnNames.includes(columnName)) return false

      const ids = params?.[paramKey]
      return Array.isArray(ids) && ids.filter(Boolean).length > 0
    })
    .map(([columnName]) => columnName)

export const isColumnFiltered = (column, params) => {
  if (!column || !params) return false

  const accessorKey = column.accessor_key

  // Check for account filters (org, object, project, segments)
  // These use pluralized param keys
  const accountParamKey = ACCOUNT_SEGMENT_PARAM_KEYS[column.database_column_name]
  if (accountParamKey && params[accountParamKey] && Array.isArray(params[accountParamKey]) && params[accountParamKey].length > 0) {
    return true
  }

  // Check for vendor filters
  if (column.database_column_name === 'vendor_name') {
    const vendors = params.vendors || []
    const excludedVendors = params.excludedVendors || []
    if (vendors.length > 0 || excludedVendors.length > 0) {
      return true
    }
  }

  // Check for reference filters (employee type)
  if (column.filter_type === 'reference') {
    const allEmployees = (params.employees || []).concat(params.excludedEmployees || [])
    const matchingEmployees = allEmployees.filter(emp =>
      emp.database_column_name === column.database_column_name
    )
    if (matchingEmployees.length > 0) {
      return true
    }
  }

  // Check for basic filter (text input)
  // Skip array values (like params.analytics which is the analytics filters array)
  const basicFilterValue = params[accessorKey]
  if (basicFilterValue && typeof basicFilterValue === 'string' && basicFilterValue !== '') {
    return true
  }

  // Check for multiselect / mcc_select (array-valued filters)
  if (Array.isArray(basicFilterValue) && basicFilterValue.length > 0) {
    return true
  }

  if (hasOperatorFilter(accessorKey, params)) {
    return true
  }

  // Check for min/max filters (date, min_max, min_max_percent)
  if (minMaxParamFilters.includes(column.filter_type)) {
    const { minKey, maxKey } = getMinMaxKeys(column)

    if (params[minKey] && params[minKey] !== '') {
      return true
    }

    if (params[maxKey] && params[maxKey] !== '') {
      return true
    }

    // Check for date option filter
    if (column.filter_type === 'date') {
      const optionKey = `${accessorKey}Option`
      if (params[optionKey] && params[optionKey] !== '') {
        return true
      }
    }
  }

  return false
}

// Function to check if an analytics column has active filters
export const isAnalyticsColumnFiltered = (analytic, params) => {
  if (!analytic || !params) return false

  const accessorKey = `analytics_${analytic.id}`

  // Check if this analytic has include/exclude options set
  // params.analytics is an array of { id, dashboard_analytic_id, options }
  if (params.analytics && Array.isArray(params.analytics)) {
    const analyticFilter = params.analytics.find(a => a.id === analytic.id)
    if (analyticFilter && analyticFilter.options && analyticFilter.options !== '' && analyticFilter.options !== 'off') {
      return true
    }
  }

  // Check for basic filter
  if (params[accessorKey] && params[accessorKey] !== '') {
    return true
  }

  if (hasOperatorFilter(accessorKey, params)) {
    return true
  }

  // Check for min/max filters if the analytic has that filter type
  if (analytic.filter_type && minMaxParamFilters.includes(analytic.filter_type)) {
    const minKey = `analyticsMin${analytic.id}`
    const maxKey = `analyticsMax${analytic.id}`

    if (params[minKey] && params[minKey] !== '') {
      return true
    }

    if (params[maxKey] && params[maxKey] !== '') {
      return true
    }

    // Check for date option filter
    if (analytic.filter_type === 'date') {
      const optionKey = `${accessorKey}Option`
      if (params[optionKey] && params[optionKey] !== '') {
        return true
      }
    }
  }

  return false
}

// Function to check if a custom column has active filters
export const isCustomColumnFiltered = (customColumn, params) => {
  if (!customColumn || !params) return false

  const baseKey = `custom_column_${customColumn.id}`

  // Check for basic filter value
  if (params[baseKey] && params[baseKey] !== '') {
    return true
  }

  if (hasOperatorFilter(baseKey, params)) {
    return true
  }

  // Check for min/max filters (for number and date columns)
  if (customColumn.column_type === 'number' || customColumn.column_type === 'date') {
    const minKey = `custom_column_min_${customColumn.id}`
    const maxKey = `custom_column_max_${customColumn.id}`

    if (params[minKey] && params[minKey] !== '') {
      return true
    }

    if (params[maxKey] && params[maxKey] !== '') {
      return true
    }

    // Check for date option filter
    if (customColumn.column_type === 'date') {
      const optionKey = `${baseKey}Option`
      if (params[optionKey] && params[optionKey] !== '') {
        return true
      }
    }
  }

  return false
}

// Preferred ordering for account columns: org, object, project, then segments
// one through eight. Mirrors the account column order used by the transactions
// table header so the column pickers default to the same business order rather
// than sorting these alphabetically ("Segment Eight" before "Segment One").
const ACCOUNT_SEGMENT_ORDER = [
  'org',
  'object',
  'project',
  'seg_one',
  'seg_two',
  'seg_three',
  'seg_four',
  'seg_five',
  'seg_six',
  'seg_seven',
  'seg_eight'
]

// The raw account segments, as a set, for the code that needs to ask "is this an
// account segment?" rather than "where does it sort?".
const ACCOUNT_SEGMENTS = new Set(ACCOUNT_SEGMENT_ORDER)

// The synthetic rollup columns: one per segment, plus the whole-account one. Matches
// TableColumn#group_scope and AccountGroup#scope_key.
const GROUP_COLUMN_NAMES = new Set([
  ...ACCOUNT_SEGMENT_ORDER.map((segment) => `${segment}_group`),
  'account_group'
])

// True for the raw account segment columns (Org, Object, Project, Segment One..Eight),
// the ones stored as an account code with both a code ("11") and a description
// ("General Fund"), so a chart can offer the choice between them. Mirrors the branch in
// Charts::BaseChart#attribute_source that resolves these to account_codes: a rollup
// column ("seg_one_group") or a filterable column of the same name is not one of them.
export const isAccountSegmentColumn = (column) =>
  column?.grouping === 'Account' &&
  !column?.filter_type &&
  ACCOUNT_SEGMENTS.has(column?.database_column_name)

// True for the user-defined rollup columns ("object_group", "account_group"). A group
// carries the same two halves the codes it rolls up do — account_groups.code and .name —
// so it answers the same question, and AccountGroups::SqlBuilder applies the choice to
// both the group labels and the codes that fall through ungrouped.
export const isAccountRollupColumn = (column) =>
  GROUP_COLUMN_NAMES.has(column?.database_column_name)

// Every column whose value is an account dimension with a code and a description, and so
// can be read either way. This is what the chart editors gate the choice on.
export const hasSegmentDisplayChoice = (column) =>
  isAccountSegmentColumn(column) || isAccountRollupColumn(column)

// The rollup columns follow their raw segments, in the same order. Left to sort
// alphabetically by display name they interleave meaninglessly, and the order
// changes the moment a municipality renames a segment.
const ACCOUNT_COLUMN_ORDER = [
  ...ACCOUNT_SEGMENT_ORDER,
  ...ACCOUNT_SEGMENT_ORDER.map((segment) => `${segment}_group`),
  'account_group'
]

// Comparator for ordering columns within a grouping. Account columns follow the
// fixed business order above; every other column stays alphabetical by display
// name. Use this anywhere columns are listed within their grouping so the
// ordering stays consistent across the app.
export const compareColumnsForDisplay = (a, b, municipality) => {
  const aRank = ACCOUNT_COLUMN_ORDER.indexOf(a.database_column_name)
  const bRank = ACCOUNT_COLUMN_ORDER.indexOf(b.database_column_name)

  if (aRank !== -1 || bRank !== -1) {
    const aOrder = aRank === -1 ? Number.MAX_SAFE_INTEGER : aRank
    const bOrder = bRank === -1 ? Number.MAX_SAFE_INTEGER : bRank
    if (aOrder !== bOrder) return aOrder - bOrder
  }

  return getColumnDisplayName(a, municipality).localeCompare(
    getColumnDisplayName(b, municipality)
  )
}

// Groups columns by their `grouping` the way the dashboard column selector does:
// "ThirdLine" first, "Calculated" last, the rest alphabetical, each group in
// compareColumnsForDisplay order. Returns [{ label, columns }].
export const groupColumnsForDisplay = (columns, municipality) => {
  const groups = (columns || []).reduce((acc, column) => {
    const key = column.grouping || 'Other'
    if (!acc[key]) acc[key] = []
    acc[key].push(column)
    return acc
  }, {})

  let keys = Object.keys(groups).sort((a, b) => a.localeCompare(b))
  if (keys.includes('ThirdLine')) {
    keys = ['ThirdLine', ...keys.filter((k) => k !== 'ThirdLine')]
  }
  if (keys.includes('Calculated')) {
    keys = [...keys.filter((k) => k !== 'Calculated'), 'Calculated']
  }

  return keys.map((key) => ({
    label: key,
    columns: groups[key].slice().sort((a, b) => compareColumnsForDisplay(a, b, municipality))
  }))
}

// Municipality-configurable segments, mapped to the setting that renames them. Org,
// object, and project are not configurable (see
// MunicipalitySetting::CONFIGURABLE_SEGMENTS), so they keep their stored names.
const SEGMENT_NAME_KEYS = {
  seg_one: 'seg_one_name',
  seg_two: 'seg_two_name',
  seg_three: 'seg_three_name',
  seg_four: 'seg_four_name',
  seg_five: 'seg_five_name',
  seg_six: 'seg_six_name',
  seg_seven: 'seg_seven_name',
  seg_eight: 'seg_eight_name'
}

const GROUP_SUFFIX = '_group'

// Display name for a column, applying the municipality's own segment names.
//
// Account-group columns are derived rather than looked up. TableColumn.name is one
// global value shared by every municipality, so "Segment One Group" cannot be stored
// as "Fund Rollup" for one district and something else for another — the substitution
// has to happen at render time, here, the same way it already does for the raw
// segment columns. This is the only place that resolves a column label; four
// hand-rolled copies of the switch below had drifted apart, and only this one knew
// about the rollup columns.
//
// "seg_one_group" resolves its base segment through the settings and appends
// "Rollup": Segment One Group -> Fund Rollup. Falling back on the stored name with a
// trailing "Group" or "Rollup" stripped keeps this working for the non-configurable
// segments (object_group -> Object Rollup) and for rows seeded under either wording.
//
// `groupingName` puts the taxonomy in front — "OCAS: Org Rollup". A rollup's name says which
// dimension it rolls up, never under which taxonomy, and both are needed to know what the
// column holds. Pass it anywhere the View is known; omitted, the label is left bare.
export const getColumnDisplayName = (column, municipality, groupingName = null) => {
  const displayName = column.display_name || column.name
  const dbName = column.database_column_name || ''
  const isGroup = dbName.endsWith(GROUP_SUFFIX)
  const segment = isGroup ? dbName.slice(0, -GROUP_SUFFIX.length) : dbName

  const settingKey = SEGMENT_NAME_KEYS[segment]
  const configured = settingKey ? municipality?.setting?.[settingKey]?.trim() : null

  if (!isGroup) return configured || displayName

  const base = configured || displayName.replace(/\s*(Group|Rollup)$/i, '')
  const label = `${base} Rollup`

  // Gated on isAccountRollupColumn, not the `_group` suffix `isGroup` uses: that also catches
  // columns which are not account rollups at all (user_data_access_group).
  if (!groupingName || !isAccountRollupColumn(column)) return label

  return `${groupingName}: ${label}`
}
